/**
 * Input Component
 * 
 * A reusable input component with various states and styles.
 * Implements accessibility features and integrates with the design system.
 */
import { forwardRef, InputHTMLAttributes } from 'react'
import { cn, twStyles } from '../../utils/tailwind-utils'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Error message to display
   */
  error?: string
  
  /**
   * Label for the input
   */
  label?: string
  
  /**
   * Helper text to display below the input
   */
  helperText?: string
  
  /**
   * Whether the input is in a loading state
   */
  isLoading?: boolean
  
  /**
   * Icon to display at the start of the input
   */
  startIcon?: React.ReactNode
  
  /**
   * Icon to display at the end of the input
   */
  endIcon?: React.ReactNode
  
  /**
   * Additional CSS classes for the wrapper
   */
  wrapperClassName?: string
}

/**
 * Input component for text entry with various states and customizations
 * 
 * @example
 * // Basic usage
 * <Input placeholder="Enter your name" />
 * 
 * @example
 * // With label and error
 * <Input 
 *   label="Email Address"
 *   type="email"
 *   error="Please enter a valid email"
 *   placeholder="example@email.com"
 * />
 * 
 * @example
 * // With icons
 * <Input 
 *   startIcon={<SearchIcon />}
 *   placeholder="Search..."
 *   endIcon={<ClearIcon onClick={handleClear} />}
 * />
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      wrapperClassName,
      type = 'text',
      error,
      label,
      helperText,
      isLoading,
      startIcon,
      endIcon,
      disabled,
      ...props
    },
    ref
  ) => {
    // Determine if the input has an error state
    const hasError = !!error
    
    return (
      <div className={cn('space-y-2', wrapperClassName)}>
        {/* Label */}
        {label && (
          <label 
            htmlFor={props.id} 
            className={cn(
              'block text-sm font-medium',
              hasError ? 'text-destructive' : 'text-foreground'
            )}
          >
            {label}
          </label>
        )}
        
        {/* Input wrapper */}
        <div className="relative">
          {/* Start icon */}
          {startIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {startIcon}
            </div>
          )}
          
          {/* Input element */}
          <input
            type={type}
            className={cn(
              twStyles.input.base,
              startIcon && 'pl-10',
              endIcon && 'pr-10',
              hasError && 'border-destructive focus-visible:ring-destructive',
              isLoading && 'opacity-70',
              className
            )}
            disabled={disabled || isLoading}
            ref={ref}
            {...props}
          />
          
          {/* End icon or loading spinner */}
          {(endIcon || isLoading) && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              {isLoading ? (
                <svg 
                  className="animate-spin h-5 w-5" 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle 
                    className="opacity-25" 
                    cx="12" 
                    cy="12" 
                    r="10" 
                    stroke="currentColor" 
                    strokeWidth="4"
                  ></circle>
                  <path 
                    className="opacity-75" 
                    fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                endIcon
              )}
            </div>
          )}
        </div>
        
        {/* Error message or helper text */}
        {(error || helperText) && (
          <p 
            className={cn(
              'text-sm',
              hasError ? 'text-destructive' : 'text-muted-foreground'
            )}
          >
            {error || helperText}
          </p>
        )}
      </div>
    )
  }
)

// Display name for debugging
Input.displayName = 'Input'

export default Input
